import { ClipboardModule } from '@angular/cdk/clipboard';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule as AngularFormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { FileUploadModule } from '@vectorsolv/vectors-ui/file-input';
import { PropertiesWebModule } from '@vsolv/packages/properties/web';
import { ContentCardModule } from '@vsolv/ui-kit/card';
import { ModalModule } from '@vsolv/ui-kit/modal';
import { UtilsModule } from '@vsolv/ui-kit/utils';
import { AccordionModule } from '@vsolv/vectors-ui/accordion';
import { AlertModule } from '@vsolv/vectors-ui/alert';
import { AppNavModule } from '@vsolv/vectors-ui/app-nav';
import { AppPageModule } from '@vsolv/vectors-ui/app-page';
import { BadgeModule } from '@vsolv/vectors-ui/badge';
import { BreadcrumbsModule } from '@vsolv/vectors-ui/breadcrumbs';
import { ButtonModule } from '@vsolv/vectors-ui/button';
import { CardModule } from '@vsolv/vectors-ui/card';
import { DialogModule } from '@vsolv/vectors-ui/dialog';
import { DatePickerModule } from '@vsolv/vectors-ui/date-picker';
import { DividerModule } from '@vsolv/vectors-ui/divider';
import { DropdownModule } from '@vsolv/vectors-ui/dropdown';
import { FormsModule } from '@vsolv/vectors-ui/forms';
import { IconsModule } from '@vsolv/vectors-ui/icons';
import { InputModule } from '@vsolv/vectors-ui/input';
import { MenuModule } from '@vsolv/vectors-ui/menu';
import { ProgressBarModule } from '@vsolv/vectors-ui/progress-bar';
import { RichTextEditorModule } from '@vsolv/vectors-ui/rich-text-editor';
import { SelectModule } from '@vsolv/vectors-ui/select';
import { SkeletonModule } from '@vsolv/vectors-ui/skeleton';
import { TabsModule } from '@vsolv/vectors-ui/tabs';
import { ThemingModule } from '@vsolv/vectors-ui/theming';
import { TooltipModule } from '@vsolv/vectors-ui/tooltip';
import { ConnectedTableComponent, ConnectedTablePage, TableModule } from '@vsolv/vectors/table';
import { AssetsWebModule } from '@wsphere/assets/web';
import { CustomerWebModule } from '@wsphere/customers/web';
import { DistributorsWebModule } from '@wsphere/distributors/web';
import { StaffWebModule } from '@wsphere/staff/web';
import { NgxEchartsModule } from 'ngx-echarts';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { NgPipesModule } from 'ngx-pipes';
import { ClaimModule } from '../claim';
import { ClaimLifecycleStepsModule } from '../claim-lifecycle-steps';
import { WarrantyCoverageModule } from '../coverage';
import {
  AddonPickerComponent,
  ConditionBlockBuilderComponent,
  ConditionBuilderComponent,
  ConditionGroupBuilderComponent,
  ConditionPreviewComponent,
  ConditionPropertyPickerComponent,
  ConditionPropertyPickerItemComponent,
  ConditionRuleBuilderComponent,
  ConditionValuePropertyPickerComponent,
  ConditionValuePropertyPickerItemComponent,
  CoverageAccordionHeaderComponent,
  CoveragesTableComponent,
  EmailPreviewComponent,
  NewDescriptiveColumnsButtonComponent,
  NewFeeButtonComponent,
  NoPlansAvailableCardComponent,
  PlanCoverageGroupBoxComponent,
  PlanCoverageMonetaryBoxComponent,
  PlanCoverageTextBoxComponent,
  PlanDetailsComponent,
  PlanPickerComponent,
  PlanPricingTableComponent,
  PolicyAssetComponent,
  PolicyCoveragesComponent,
  PolicyDetailsCardComponent,
  PolicyDetailsComponent,
  PolicyDocumentsComponent,
  PolicyFilterPickerComponent,
  PolicyPickerComponent,
  PolicyStatsComponent,
  PolicyTermComponent,
  PolicyTermPickerComponent,
  ShippingAddressPropertyPickerComponent,
  ShippingAddressPropertyPickerItemComponent,
  FeeRecipientPickerComponent,
} from './components';
import {
  ArchivePolicyDialog,
  CopyPolicyToEnvironmentDialog,
  CopyCoverageDialog,
  CopyTermConfigDialog,
  CreateEmailTemplateDialog,
  CreateNewVersionDialog,
  DeleteCoverageDialog,
  DeleteDimensionDialog,
  DeleteElementDialog,
  DeletePlanDialog,
  DeletePolicyDialog,
  DimensionDialog,
  DuplicatePlanDialog,
  EditCancelationRuleDialog,
  EditEmailTemplateDialog,
  EditPlanConditionDialog,
  ElementDialog,
  PartyDialog,
  DeletePartyDialog,
  EmailPreviewDialog,
  ExportPlanCoverageConfigsDialog,
  GenerateLinkDialog,
  LinkToPolicyDialog,
  ManagePlanGroupsDialog,
  PlanDialog,
  PlanTermConditionsDialog,
  PolicyDialog,
  PreviewGroupAddonsDialog,
  PreviewPDFDialog,
  PublishPolicyDialog,
  RestorePolicyDialog,
} from './dialogs';
import { CoverageGuard, PlanGuard, PolicyGuard } from './guards';
import {
  CoveragePage,
  PoliciesPage,
  PolicyCancelationPage,
  PolicyClaimLifecyclePage,
  PolicyCommunicationsPage,
  PolicyDetailsPage,
  PolicyDimensionsPage,
  PolicyPartiesPage,
  PolicyDocumentsPage,
  PolicyPage,
  PolicyPlansPage,
  PolicyRatingMatrixPage,
} from './pages';
import { ConditionPropertyPipe, DurationPipe, InstallmentPipe, IsSelectedPipe, ValuePathLabelPipe } from './pipes';
import { CoverageResolver, PlanResolver, PolicyResolver } from './resolvers';
import { CoverageService, EnvironmentService, PolicyService, RatingMatrixService } from './services';

@NgModule({
  imports: [
    ConnectedTablePage,
    ConnectedTableComponent,
    CommonModule,
    ModalModule,
    ContentCardModule,
    AngularFormsModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    UtilsModule,
    AppPageModule,
    BadgeModule,
    IconsModule,
    ButtonModule,
    ThemingModule,
    TableModule,
    DialogModule,
    DatePickerModule,
    DividerModule,
    AppNavModule,
    CardModule,
    BreadcrumbsModule,
    WarrantyCoverageModule,
    InputModule,
    FormsModule,
    ClaimModule,
    SelectModule,
    RichTextEditorModule,
    MenuModule,
    ProgressBarModule,
    SkeletonModule,
    TooltipModule,
    AlertModule,
    IconsModule,
    TabsModule,
    AssetsWebModule,
    CustomerWebModule,
    DropdownModule,
    BadgeModule,
    ClipboardModule,
    NgxMaskPipe,
    NgxMaskDirective,
    DistributorsWebModule,
    OverlayModule,
    PropertiesWebModule,
    OverlayModule,
    AccordionModule,
    FileUploadModule,
    ClaimLifecycleStepsModule,
    StaffWebModule,
    NgxEchartsModule,
    NgPipesModule,
  ],
  providers: [
    PolicyGuard,
    PolicyResolver,
    PlanGuard,
    PlanResolver,
    PolicyService,
    CoverageGuard,
    CoverageResolver,
    CoverageService,
    EnvironmentService,
    RatingMatrixService,
  ],
  declarations: [
    // Pages
    PoliciesPage,
    PolicyPage,
    PolicyDetailsPage,
    PolicyPlansPage,
    PolicyCancelationPage,
    PolicyDocumentsPage,
    PolicyCommunicationsPage,
    PolicyDimensionsPage,
    PolicyPartiesPage,
    PolicyRatingMatrixPage,
    PolicyClaimLifecyclePage,
    CoveragePage,

    // Dialogs
    PolicyDialog,
    PublishPolicyDialog,
    ArchivePolicyDialog,
    RestorePolicyDialog,
    CopyPolicyToEnvironmentDialog,
    EmailPreviewDialog,
    DeletePolicyDialog,
    CopyCoverageDialog,
    CreateEmailTemplateDialog,
    DeleteCoverageDialog,
    PreviewPDFDialog,
    CopyCoverageDialog,
    EditEmailTemplateDialog,
    EditCancelationRuleDialog,
    PlanDialog,
    PlanTermConditionsDialog,
    EditPlanConditionDialog,
    ExportPlanCoverageConfigsDialog,
    DeletePlanDialog,
    DuplicatePlanDialog,
    GenerateLinkDialog,
    LinkToPolicyDialog,
    ManagePlanGroupsDialog,
    PreviewGroupAddonsDialog,
    CopyTermConfigDialog,
    CreateNewVersionDialog,
    DimensionDialog,
    ElementDialog,
    PartyDialog,
    DeletePartyDialog,
    DeleteDimensionDialog,
    DeleteElementDialog,

    // Pipes
    ConditionPropertyPipe,
    DurationPipe,
    InstallmentPipe,
    IsSelectedPipe,
    ValuePathLabelPipe,

    // Components
    PolicyDetailsComponent,
    PolicyAssetComponent,
    PolicyTermComponent,
    PolicyCoveragesComponent,
    PolicyDocumentsComponent,
    PolicyStatsComponent,
    CoveragesTableComponent,
    PlanDetailsComponent,
    PlanPricingTableComponent,
    PlanCoverageTextBoxComponent,
    PlanCoverageMonetaryBoxComponent,
    PlanCoverageGroupBoxComponent,
    ConditionRuleBuilderComponent,
    ConditionBlockBuilderComponent,
    ConditionGroupBuilderComponent,
    ConditionBuilderComponent,
    ConditionPreviewComponent,
    PlanPickerComponent,
    PolicyPickerComponent,
    PolicyDetailsCardComponent,
    NoPlansAvailableCardComponent,
    EmailPreviewComponent,
    ConditionPropertyPickerComponent,
    ConditionPropertyPickerItemComponent,
    ShippingAddressPropertyPickerItemComponent,
    FeeRecipientPickerComponent,
    ShippingAddressPropertyPickerComponent,
    AddonPickerComponent,
    PreviewGroupAddonsDialog,
    ConditionValuePropertyPickerItemComponent,
    ConditionValuePropertyPickerComponent,
    PolicyTermPickerComponent,
    PolicyFilterPickerComponent,
    NewDescriptiveColumnsButtonComponent,
    NewFeeButtonComponent,
    CoverageAccordionHeaderComponent,
  ],
  exports: [
    // Pages
    PoliciesPage,
    PolicyPage,
    PolicyDetailsPage,
    PolicyPlansPage,
    PolicyCancelationPage,
    PolicyDocumentsPage,
    PolicyCommunicationsPage,
    PolicyDimensionsPage,
    PolicyPartiesPage,
    CoveragePage,

    // Dialogs
    PolicyDialog,
    PublishPolicyDialog,
    ArchivePolicyDialog,
    RestorePolicyDialog,
    CopyPolicyToEnvironmentDialog,
    EmailPreviewDialog,
    DeletePolicyDialog,
    CopyCoverageDialog,
    CreateEmailTemplateDialog,
    DeleteCoverageDialog,
    PreviewPDFDialog,
    CopyCoverageDialog,
    EditEmailTemplateDialog,
    EditCancelationRuleDialog,
    PlanDialog,
    EditPlanConditionDialog,
    ExportPlanCoverageConfigsDialog,
    DeletePlanDialog,
    DuplicatePlanDialog,
    GenerateLinkDialog,
    LinkToPolicyDialog,
    ManagePlanGroupsDialog,

    //Pipes
    DurationPipe,
    InstallmentPipe,

    // Components
    PlanCoverageTextBoxComponent,
    PlanCoverageMonetaryBoxComponent,
    PlanCoverageGroupBoxComponent,
    ConditionRuleBuilderComponent,
    ConditionBlockBuilderComponent,
    ConditionGroupBuilderComponent,
    ConditionBuilderComponent,
    ConditionPreviewComponent,
    PlanPickerComponent,
    PolicyPickerComponent,
    PolicyDetailsCardComponent,
    AddonPickerComponent,
    PolicyFilterPickerComponent,
    PreviewGroupAddonsDialog,
  ],
})
export class PolicyModule {}
